import type { SagaProgress, SagaProgressStatus } from 'types/sagaProgress';
import { usePolledResource, type PolledResourceDenialReason } from './usePolledResource';

const TERMINAL_STATUSES = new Set<SagaProgressStatus>(['completed', 'failed', 'compensated']);

export type SagaProgressDenialReason = PolledResourceDenialReason;

export interface UseSagaProgressOptions {
  /** Polling runs only while `true`. Flip to `false` to pause (e.g. dialog closed). */
  active: boolean;
}

export interface UseSagaProgressResult {
  progress: SagaProgress | null;
  loading: boolean;
  /** Non-null while consecutive fetch failures (other than 403/404) are ongoing. */
  error: Error | null;
  /** Set once a 403 or 404 is seen; polling has stopped permanently. */
  deniedReason: SagaProgressDenialReason | null;
  refetch: () => void;
}

const isTerminalProgress = (progress: SagaProgress | null): boolean =>
  progress != null && TERMINAL_STATUSES.has(progress.status);

/**
 * Polls a saga-progress fetcher until the run reaches a terminal status — `usePolledResource`'s
 * cadence, hidden-tab slowdown, 404 grace, back-off and last-good-snapshot retention, applied to
 * one saga run.
 */
export function useSagaProgress(
  fetcher: () => Promise<SagaProgress | null>,
  { active }: UseSagaProgressOptions
): UseSagaProgressResult {
  const { data, loading, error, deniedReason, refetch } = usePolledResource(fetcher, {
    active,
    isTerminal: isTerminalProgress,
  });
  return { progress: data, loading, error, deniedReason, refetch };
}

export default useSagaProgress;
